import React, { useState, useEffect, FormEvent, useCallback } from "react";
import { WhiteBoxWrapper } from "./Login.style";
import DeviceCard, { Device } from "./DeviceCard";
import { Card, Col, Nav, Row, Form, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { fetchAPI } from "../utils/fetchAPI";
import * as XLSX from "xlsx";
import { FaFileExcel } from "react-icons/fa";

const STORAGE_KEY = "deviceFilters";

interface DeviceFilters {
  name: string;
  status: string;
  returnable: string;
  category: string;
  type: string;
  description: string;
}

const DevicesList = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [categoryList, setCategoryList] = useState<string[]>([]);
  const [typeList, setTypeList] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<DeviceFilters>(() => {
    // Wczytaj zapisane filtry z localStorage przy inicjalizacji
    const savedFilters = localStorage.getItem(STORAGE_KEY);
    return savedFilters
      ? JSON.parse(savedFilters)
      : { name: "", status: "", returnable: "", category: "", type: "", description: "" };
  });

  const fetchDevices = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const queryParams = new URLSearchParams();
      if (filters.name) queryParams.append("name", filters.name);
      if (filters.description) queryParams.append("description", filters.description);

      // Poprawiona logika dla returnable
      if (filters.returnable !== "") {
        queryParams.append("returnable", filters.returnable);
      }

      // Poprawiona logika dla statusu
      if (filters.status !== "") {
        queryParams.append("status", filters.status);
      }

      if (filters.category) queryParams.append("category", filters.category);
      if (filters.type) queryParams.append("type", filters.type);

      console.log("Query params:", queryParams.toString());
      const data = await fetchAPI("GET", `devices.php?${queryParams.toString()}`);
      console.log("Server response:", data);
      console.log("Response type:", typeof data);
      console.log("Response keys:", data ? Object.keys(data) : "null");

      if (!data) {
        console.log("Data is null, throwing error");
        throw new Error("Nie udało się połączyć z serwerem");
      }

      if (data.success) {
        console.log("Success is true, setting data");
        setDevices(data.data || []);
        setCategoryList(data.categoryList || []);
        setTypeList(data.typeList || []);
      } else {
        console.log("Success is false, setting empty data with message:", data.message);
        setDevices([]);
        setCategoryList([]);
        setTypeList([]);
        setErrorMessage(data.message || "Nie udało się pobrać urządzeń.");
      }
    } catch (error: any) {
      console.log("Caught error:", error);
      console.log("Error message:", error.message);
      console.log("Error response:", error.response);
      setErrorMessage(
        error.message ||
          "Wystąpił błąd podczas pobierania danych: " + (error.response?.data?.message || error.toString())
      );
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // Zapisz filtry w localStorage przy każdej zmianie
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    fetchDevices();
  };

  const handleFilterChange = (key: keyof DeviceFilters, value: string) => {
    console.log("Filter change:", key, value);
    setFilters((prev: DeviceFilters) => {
      const newFilters = { ...prev, [key]: value };
      // Jeśli zmieniamy typ na stacjonarne, resetujemy status
      if (key === "returnable" && value === "0") {
        newFilters.status = "";
      }
      return newFilters;
    });
  };

  const generateExcel = () => {
    // Przygotuj dane do eksportu
    const exportData = devices.map((device) => ({
      Nazwa: device.name,
      Opis: device.description,
      Status: device.status === 1 ? "Aktywne" : device.status === 2 ? "W naprawie" : "Uszkodzone",
      Typ: device.returnable === 1 ? "Stacjonarne" : "Wydawka",
      Lokalizacja: device.category,
      Rodzaj: device.type,
    }));

    // Utwórz nowy arkusz
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Spis Wyposażenia");

    // Zapisz plik
    XLSX.writeFile(wb, "spis_wyposazenia.xlsx");
  };

  return (
    <div>
      <WhiteBoxWrapper>
        <Row className="align-items-center">
          <Col xs={9}>
            <h2 className="text-center">
              <b>Spis Wyposażenia</b>
            </h2>
          </Col>
          <Col xs={3}>
            <h2>
              <Nav.Link className="btn btn-primary" as={Link} to="/deviceadd">
                +
              </Nav.Link>
            </h2>
          </Col>
        </Row>
      </WhiteBoxWrapper>
      <Card className="p-2 bg-brand-green">
        <Form onSubmit={handleFormSubmit}>
          <Row>
            <Col xs={6} md={3}>
              <Form.Group>
                <Form.Label>Nazwa</Form.Label>
                <Form.Control
                  type="text"
                  value={filters.name}
                  onChange={(e) => handleFilterChange("name", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={6} md={3}>
              <Form.Group>
                <Form.Label>Opis</Form.Label>
                <Form.Control
                  type="text"
                  value={filters.description}
                  onChange={(e) => handleFilterChange("description", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={6} md={3}>
              <Form.Group>
                <Form.Label>Typ</Form.Label>
                <Form.Select
                  value={filters.returnable}
                  onChange={(e) => handleFilterChange("returnable", e.target.value)}
                >
                  <option value="">Wszystkie</option>
                  <option value="1">Stacjonarne</option>
                  <option value="0">Wydawka</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={6} md={3}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select value={filters.status} onChange={(e) => handleFilterChange("status", e.target.value)}>
                  <option value="">Wszystkie</option>
                  <option value="1">Aktywne</option>
                  <option value="2">W naprawie</option>
                  <option value="3">Uszkodzone</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={6} md={3}>
              <Form.Group>
                <Form.Label>Lokalizacja</Form.Label>
                <Form.Select value={filters.category} onChange={(e) => handleFilterChange("category", e.target.value)}>
                  <option value="">Wszystkie</option>
                  {categoryList.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={6} md={3}>
              <Form.Group>
                <Form.Label>Rodzaj</Form.Label>
                <Form.Select value={filters.type} onChange={(e) => handleFilterChange("type", e.target.value)}>
                  <option value="">Wszystkie</option>
                  {typeList.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Form>
        <div className="row">
          <div className="col-12 d-flex justify-content-end">
            <Button variant="primary" onClick={generateExcel}>
              <FaFileExcel className="me-2" /> Generuj Excel
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-2 mt-2 bg-brand-gren">
        {isLoading && <p>Ładowanie danych...</p>}
        {errorMessage && <p className="error">{errorMessage}</p>}
        {!isLoading && !errorMessage && devices.length > 0 && (
          <Row>
            {devices.map((device) => (
              <Col key={device.id} xs={12} md={4}>
                <DeviceCard data={device} />
              </Col>
            ))}
          </Row>
        )}
        {!isLoading && devices.length === 0 && !errorMessage && (
          <p>Nie znaleziono urządzeń spełniających podane kryteria.</p>
        )}
      </Card>
    </div>
  );
};

export default DevicesList;
